import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from '../lib/axios';
import { Plus, Users as UsersIcon, Search } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const Classrooms = () => {
  const { user } = useAuth();
  const [classrooms, setClassrooms] = useState([]);
  const [children, setChildren] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newClassroom, setNewClassroom] = useState({
    name: '',
    age_group: '',
    ratio_notes: '',
  });

  useEffect(() => {
    fetchClassrooms();
  }, []);

  const fetchClassrooms = async () => {
    try {
      const [classroomsRes, childrenRes] = await Promise.all([
        axios.get('/classrooms'),
        axios.get('/children'),
      ]);
      
      setClassrooms(classroomsRes.data);
      
      // Group children by classroom
      const childrenByClassroom = {};
      childrenRes.data.forEach(child => {
        if (!childrenByClassroom[child.classroom_id]) {
          childrenByClassroom[child.classroom_id] = [];
        }
        childrenByClassroom[child.classroom_id].push(child);
      });
      setChildren(childrenByClassroom);
    } catch (error) {
      console.error('Error fetching classrooms:', error);
      toast.error('Failed to load classrooms');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClassroom = async (e) => {
    e.preventDefault();
    try {
      await axios.post('/classrooms', newClassroom);
      toast.success('Classroom created successfully!');
      setIsAddDialogOpen(false);
      setNewClassroom({ name: '', age_group: '', ratio_notes: '' });
      fetchClassrooms();
    } catch (error) {
      console.error('Error creating classroom:', error);
      toast.error(error.response?.data?.detail || 'Failed to create classroom');
    }
  };

  const filteredClassrooms = classrooms.filter(classroom =>
    classroom.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    classroom.age_group.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="classrooms-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Classrooms
          </h1>
          <p className="text-slate-600 mt-1">Manage your center's classrooms</p>
        </div>
        {user?.role === 'admin' && (
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-full" data-testid="add-classroom-button">
                <Plus className="w-4 h-4 mr-2" />
                Add Classroom
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Classroom</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddClassroom} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="name">Classroom Name</Label>
                  <Input
                    id="name"
                    value={newClassroom.name}
                    onChange={(e) => setNewClassroom({ ...newClassroom, name: e.target.value })}
                    placeholder="e.g., Toddler Room A"
                    required
                    className="mt-1.5"
                    data-testid="classroom-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="age_group">Age Group</Label>
                  <Input
                    id="age_group"
                    value={newClassroom.age_group}
                    onChange={(e) => setNewClassroom({ ...newClassroom, age_group: e.target.value })}
                    placeholder="e.g., 12-24 months"
                    required
                    className="mt-1.5"
                    data-testid="classroom-age-group-input"
                  />
                </div>
                <div>
                  <Label htmlFor="ratio_notes">Ratio Notes (Optional)</Label>
                  <Input
                    id="ratio_notes"
                    value={newClassroom.ratio_notes}
                    onChange={(e) => setNewClassroom({ ...newClassroom, ratio_notes: e.target.value })}
                    placeholder="e.g., 1:5 teacher to child ratio"
                    className="mt-1.5"
                    data-testid="classroom-ratio-input"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button type="submit" className="flex-1" data-testid="submit-classroom-button">
                    Create Classroom
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setIsAddDialogOpen(false)}
                    data-testid="cancel-classroom-button"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          type="search"
          placeholder="Search classrooms..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
          data-testid="search-classrooms-input"
        />
      </div>

      {/* Classrooms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredClassrooms.map((classroom) => (
          <Link
            key={classroom.id}
            to={`/classrooms/${classroom.id}`}
            className="bg-white rounded-xl border border-slate-100 p-6 hover:border-primary/20 hover:shadow-md transition-all group"
            data-testid={`classroom-card-${classroom.id}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <UsersIcon className="w-6 h-6 text-primary" />
              </div>
              <span className="text-xs font-medium text-slate-600 bg-slate-100 px-2 py-1 rounded-full">
                {children[classroom.id]?.length || 0} children
              </span>
            </div>
            <h3 className="text-lg font-bold text-slate-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {classroom.name}
            </h3>
            <p className="text-sm text-slate-600 mb-2">{classroom.age_group}</p>
            {classroom.ratio_notes && (
              <p className="text-xs text-slate-500 mt-2">{classroom.ratio_notes}</p>
            )}
          </Link>
        ))}
      </div>

      {filteredClassrooms.length === 0 && (
        <div className="text-center py-12">
          <UsersIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No classrooms found</h3>
          <p className="text-slate-600 mb-4">
            {searchTerm ? 'Try adjusting your search' : 'Get started by creating your first classroom'}
          </p>
          {user?.role === 'admin' && !searchTerm && (
            <Button onClick={() => setIsAddDialogOpen(true)} data-testid="empty-state-add-button">
              <Plus className="w-4 h-4 mr-2" />
              Add First Classroom
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default Classrooms;
